import dayjs, { type Dayjs } from 'dayjs';
import type { FilterConfig } from '@/components/FilterBar/FilterBar';
import {
  createSearchFilter,
  createSelectFilter,
} from '@/components/FilterBar/builders';
import { CHANNEL_GROUP_OPTIONS } from './constants';
import { resolveOrderDatePreset } from '@/components/DateRangePresetPicker/presets';
import { DateRangePresetPicker } from '@/components/DateRangePresetPicker';
import { DELIVERY_REGION_OPTIONS, isDeliveryRegionValue } from '@/constants/deliveryRegion';
import type { OrderListQuery } from '@/services/queryKeys';

export const ORDER_DATE_URL_FORMAT = 'DD/MM/YYYY';

export type OrderFilterKey =
  | 'search'
  | 'order_number'
  | 'customer'
  | 'status'
  | 'status_group'
  | 'channel_group'
  | 'date_from'
  | 'date_to'
  | 'date_preset'
  | 'total_from'
  | 'total_to'
  | 'created_by'
  | 'fee_payer'
  | 'has_customer_note'
  | 'prepaid'
  | 'payment_state'
  // TCR-1967 — đã/chưa phát hành hoá đơn GTGT.
  | 'invoice_state'
  // TCR-1668 — lọc theo chi nhánh. Tên key khớp đúng query param `storeId` mà
  // `GET /orders` đã nhận sẵn (xem `OrderListQuery` ở BE); đặt `store_id` ở FE cho đúng
  // quy ước snake_case của URL, việc đổi tên sang `storeId` làm ở `buildOrderApiFilters`.
  | 'store_id'
  // TCR-2130 — kênh khách B2B (Sỉ/GT/MT) ở MỌI kênh bán, khác `channel_group` chỉ tách được B2B POS.
  | 'b2b_channel'
  // TCR-2129 — kế toán lọc đơn có chiết khấu khác chính sách (BE `has_discount_deviation`).
  | 'has_discount_deviation'
  // TCR-2625 — đơn có / không có SP thuộc danh mục Gift.
  | 'gift_filter'
  // TCR-2629 — lọc theo miền giao hàng của đơn.
  | 'delivery_region';

export const ORDER_FILTER_KEYS: readonly OrderFilterKey[] = [
  'search',
  'order_number',
  'customer',
  'channel_group',
  'status_group',
  'status',
  'date_from',
  'date_to',
  'date_preset',
  'total_from',
  'total_to',
  'created_by',
  'fee_payer',
  'has_customer_note',
  'prepaid',
  'payment_state',
  'invoice_state',
  'store_id',
  'b2b_channel',
  'has_discount_deviation',
  'gift_filter',
  'delivery_region',
] as const;

/**
 * TCR-1092 vòng 2 — filter "Trạng thái thanh toán".
 *
 * URL giữ token ngắn (`paid` / `unpaid`) thay vì tên enum: nhãn người dùng thấy không map
 * 1-1 với `payment_state` của đơn — "Chưa thanh toán" phủ CẢ `PENDING` lẫn `UNPAID`
 * (xem `ORDER_PAYMENT_STATE_LABEL`, hai key cùng một nhãn). Gửi thẳng 1 enum sẽ bỏ sót
 * một nửa số đơn chưa trả tiền.
 *
 * Các trạng thái còn lại (`PARTIAL_PAID`, `FAILED`, `EXPIRED`, `REFUNDED`) cố ý KHÔNG
 * thuộc lựa chọn nào — gom chúng vào "đã/chưa thanh toán" là bịa nghĩa cho nhãn.
 */
export const ORDER_PAYMENT_STATE_FILTER_OPTIONS = [
  { label: 'Chưa thanh toán', value: 'unpaid' },
  { label: 'Đã thanh toán', value: 'paid' },
] as const;

/**
 * TCR-1967 — filter "Trạng thái hoá đơn". "Đã phát hành" = đơn có ít nhất một hoá đơn GTGT đã
 * được nhà cung cấp cấp số; BE tra ở `OrderEInvoiceLinkService`, KHÔNG đọc
 * `orders.e_invoice.status` (cờ đó kẹt `pending`).
 */
/** TCR-2130 — lọc theo kênh khách B2B (`Order.b2b_channel`). */
export const ORDER_B2B_CHANNEL_FILTER_OPTIONS = [
  { label: 'Sỉ', value: 'SI' },
  { label: 'GT (đã thanh toán trước)', value: 'GT' },
  { label: 'MT', value: 'MT' },
] as const;

/**
 * TCR-2625 — filter "Quà tặng": đơn có ít nhất 1 SP thuộc danh mục Gift (kể cả thành phần combo
 * thật). Dòng "quà khuyến mãi" (`is_gift`) mà SP không thuộc danh mục Gift KHÔNG tính.
 */
export const ORDER_GIFT_FILTER_OPTIONS = [
  { label: 'Có quà tặng', value: 'has' },
  { label: 'Không có quà tặng', value: 'none' },
] as const;

export const ORDER_INVOICE_STATE_FILTER_OPTIONS = [
  { label: 'Đã phát hành hoá đơn', value: 'issued' },
  { label: 'Chưa phát hành hoá đơn', value: 'not_issued' },
] as const;

const PAYMENT_STATE_FILTER_TO_ENUMS: Record<string, string> = {
  unpaid: 'PENDING,UNPAID',
  paid: 'PAID',
};

/**
 * TCR-1092: chuẩn hoá 4 filter mới từ chuỗi URL sang param API.
 *
 * Tách riêng vì có HAI nơi tiêu thụ: query danh sách (`useOrderList`) và query
 * export ("Xuất Excel"). Trước đây mỗi nơi tự viết, và đường export quên hẳn 4
 * filter này — người dùng lọc còn 12 đơn rồi xuất ra file đầy đủ mọi đơn mà
 * không có cảnh báo. Một hàm dùng chung để hai đường không bao giờ lệch nữa.
 *
 * Giá trị rác trên URL (người dùng sửa tay) → `undefined`, KHÔNG gửi lên BE.
 */
export function buildOrderApiFilters(
  filters: Pick<
    Record<OrderFilterKey, string>,
    | 'created_by'
    | 'fee_payer'
    | 'has_customer_note'
    | 'prepaid'
    | 'payment_state'
    | 'invoice_state'
    | 'store_id'
    | 'b2b_channel'
    | 'has_discount_deviation'
    | 'gift_filter'
    | 'delivery_region'
  >,
): Pick<
  OrderListQuery,
  | 'created_by'
  | 'fee_payer'
  | 'has_customer_note'
  | 'prepaid'
  | 'payment_state'
  | 'invoice_state'
  | 'storeId'
  | 'b2b_channel'
  | 'has_discount_deviation'
  | 'gift_filter'
  | 'delivery_region'
> {
  return {
    gift_filter:
      filters.gift_filter === 'has' || filters.gift_filter === 'none' ? filters.gift_filter : undefined,
    // TCR-2629 — miền giao hàng: chỉ nhận `north` | `central` | `south`, giá trị rác
    // trên URL bị bỏ để BE không trả bảng lệch.
    delivery_region: isDeliveryRegionValue(filters.delivery_region)
      ? filters.delivery_region
      : undefined,
    created_by: filters.created_by || undefined,
    payment_state: PAYMENT_STATE_FILTER_TO_ENUMS[filters.payment_state] ?? undefined,
    invoice_state:
      filters.invoice_state === 'issued' || filters.invoice_state === 'not_issued'
        ? filters.invoice_state
        : undefined,
    fee_payer:
      filters.fee_payer === 'seller' || filters.fee_payer === 'buyer'
        ? filters.fee_payer
        : undefined,
    has_customer_note:
      filters.has_customer_note === 'true' || filters.has_customer_note === 'false'
        ? filters.has_customer_note
        : undefined,
    prepaid:
      filters.prepaid === 'true' || filters.prepaid === 'false' ? filters.prepaid : undefined,
    // TCR-1668 — key URL `store_id` (snake_case, thống nhất với các filter khác) đổi sang tên
    // query param `storeId` mà BE đã nhận sẵn.
    storeId: filters.store_id || undefined,
    b2b_channel:
      filters.b2b_channel === 'SI' || filters.b2b_channel === 'GT' || filters.b2b_channel === 'MT'
        ? filters.b2b_channel
        : undefined,
    // TCR-2129 — chỉ nhận 'true'; giá trị rác trên URL bị bỏ.
    has_discount_deviation: filters.has_discount_deviation === 'true' ? 'true' : undefined,
  };
}

/** URL giữ chuỗi; đổi sang number cho API, bỏ qua giá trị rác. */
export function parseOrderAmount(value: string): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

export {
  ORDER_DATE_PRESET_LABEL,
  ORDER_DATE_PRESET_DEFAULT,
  ORDER_DATE_PRESET_THIS_MONTH,
  ORDER_DATE_PRESETS,
  ORDER_DATE_PRESET_GROUPS,
  isOrderDatePresetKey,
} from '@/components/DateRangePresetPicker/presets';

/**
 * Khoảng ngày đang áp dụng. Mặc định của trang lưu PRESET chứ không lưu ngày tuyệt đối:
 * snapshot filter nằm ở localStorage nên nếu ghi `01/07/2026 - 31/07/2026`, người dùng
 * giữ phiên đăng nhập sang tháng 8 sẽ vẫn bị lọc tháng 7 và thấy bảng trống mà không
 * hiểu vì sao. Preset được resolve lại mỗi lần mount.
 *
 * Preset key lạ → trả về khoảng rỗng (không lọc ngày), KHÔNG âm thầm rơi về mặc định.
 */
export function resolveOrderDateRange(
  filters: Pick<Record<OrderFilterKey, string>, 'date_from' | 'date_to' | 'date_preset'>,
  now?: Dayjs,
): { from: string; to: string } {
  if (filters.date_preset) {
    const range = resolveOrderDatePreset(filters.date_preset, now);
    if (!range) return { from: '', to: '' };
    return {
      from: range[0].format(ORDER_DATE_URL_FORMAT),
      to: range[1].format(ORDER_DATE_URL_FORMAT),
    };
  }
  return { from: filters.date_from, to: filters.date_to };
}

/** Patch chỉ chứa các key thực sự đổi; '' nghĩa là xoá khỏi URL. */
export type OrderFilterPatch = Partial<Record<OrderFilterKey, string>>;

interface BuildOpts {
  /** Filter ĐÃ applied (đọc từ URL) — instant-apply nên không còn tầng draft riêng. */
  filters: Record<OrderFilterKey, string>;
  onApplyFilter: (patch: OrderFilterPatch) => void;
  /**
   * TCR-1092: option cho filter "Người tạo đơn". Giá trị `value` PHẢI là
   * `staff_id` (== `_id` nhân viên, theo `staff.service.ts#mapUserToStaff`) —
   * BE đọc query `created_by` và so với `Order.created_by` (cùng field mà cột
   * "Người tạo đơn" render qua `created_by_name`), nên filter và cột luôn khớp.
   * Rỗng nếu trang gọi chưa load được danh sách nhân viên (vẫn loading).
   */
  staffOptions?: { label: string; value: string }[];
  /**
   * TCR-1668: option cho filter "Chi nhánh". `value` PHẢI là `store_id` (== `_id` chi
   * nhánh) — BE đọc query `storeId` và so thẳng với `Order.store_id`, cùng field mà cột
   * "Chi nhánh" render qua `store_name`, nên filter và cột luôn khớp. Rỗng nếu trang gọi
   * chưa load xong danh sách chi nhánh.
   */
  storeOptions?: { label: string; value: string }[];
}

export function parseOrderDateDraft(value: string): Dayjs | null {
  if (!value) return null;
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (match) {
    const [, dd, mm, yyyy] = match;
    const parsed = dayjs(`${yyyy}-${mm}-${dd}`);
    return parsed.isValid() ? parsed : null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

export function orderDateUrlToApiDate(value?: string): string | undefined {
  if (!value) return undefined;
  return parseOrderDateDraft(value)?.format('YYYY-MM-DD');
}

export function normalizeOrderDateUrlValue(value?: string): string | undefined {
  if (!value) return undefined;
  return parseOrderDateDraft(value)?.format(ORDER_DATE_URL_FORMAT);
}

/**
 * Nhóm lọc CHÍNH — nằm thẳng trên thanh công cụ.
 *
 * TCR-1092 vòng 2: chỉ còn 3 ô. Vòng 1 đặt cả 7 filter cạnh nhau, tất cả cùng hiện
 * placeholder "Tất cả" và không có nhãn, nên tester không đoán nổi ô nào lọc cái gì.
 * Phần còn lại chuyển vào drawer "Bộ lọc" (xem `buildOrderAdvancedFilters`) — ở đó mỗi
 * ô xếp dọc kèm nhãn.
 */
export function buildOrderPrimaryFilters({
  filters,
  onApplyFilter,
  /**
   * true khi nhóm này bị gộp vào drawer "Bộ lọc" trên mobile (xem Orders/index.tsx,
   * `filterDrawerFilterConfigs`) thay vì nằm ngang trên thanh công cụ desktop — ô lọc
   * phải xếp full width như các ô nâng cao khác trong drawer, không giữ px cố định
   * (260/160) vốn tính cho hàng ngang.
   */
  stacked = false,
}: BuildOpts & { stacked?: boolean }): FilterConfig[] {
  // Range hiển thị lấy từ preset đã resolve, không đọc thẳng date_from/date_to.
  const activeRange = resolveOrderDateRange(filters);

  return [
    createSearchFilter({
      key: 'search',
      label: 'Tìm kiếm',
      placeholder: 'Tìm mã đơn, mã vận đơn, SĐT, mã ERP, tên SP',
      // TCR-2631 — 380 để chữ gợi ý (có thêm "mã ERP, tên SP") không bị cắt.
      width: stacked ? '100%' : 380,
      value: filters.search,
      // DebouncedSearchInput đã gom phím trong 300ms nên apply ngay không spam URL.
      onChange: (v) => onApplyFilter({ search: v }),
    }),
    createSelectFilter({
      key: 'channel_group',
      label: 'Kênh',
      placeholder: 'Tất cả kênh',
      width: stacked ? '100%' : 160,
      value: filters.channel_group,
      onChange: (v) => onApplyFilter({ channel_group: v }),
      options: CHANNEL_GROUP_OPTIONS,
    }),
    {
      // FilterConfig tự dựng thay vì createDateRangeFilter: builder đó đang được ~50
      // trang khác dùng, sửa nó là đụng cả hệ thống. Preset hiện chỉ áp cho Orders;
      // module nào cần thì nâng DateRangePresetPicker lên components/ sau.
      key: 'date_range',
      label: 'Thời gian',
      type: 'daterange' as const,
      render: () => (
        <DateRangePresetPicker
          preset={filters.date_preset}
          from={activeRange.from}
          to={activeRange.to}
          onChange={onApplyFilter}
        />
      ),
    },
  ];
}

/** Nhóm lọc NÂNG CAO — hiện trong drawer "Bộ lọc" (TCR-1092 vòng 2). */
export function buildOrderAdvancedFilters({
  filters,
  onApplyFilter,
  staffOptions = [],
  storeOptions = [],
}: BuildOpts): FilterConfig[] {
  return [
    // TCR-2625 — ticket yêu cầu "cạnh Kênh / Thời gian": đứng đầu drawer, ngay sau 2 ô đó
    // (mobile: drawer ghép nhóm chính + nhóm nâng cao).
    createSelectFilter({
      key: 'gift_filter',
      label: 'Quà tặng',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.gift_filter,
      onChange: (v) => onApplyFilter({ gift_filter: v }),
      options: [...ORDER_GIFT_FILTER_OPTIONS],
    }),
    // TCR-1668 — đặt ĐẦU nhóm nâng cao: chi nhánh là lát cắt rộng nhất trong drawer (thu hẹp
    // tập đơn trước khi lọc tiếp theo người tạo/thanh toán), nên để trên cùng cho đúng thứ tự
    // người dùng nghĩ.
    createSelectFilter({
      key: 'store_id',
      label: 'Chi nhánh',
      placeholder: 'Tất cả chi nhánh',
      width: '100%',
      value: filters.store_id,
      onChange: (v) => onApplyFilter({ store_id: v }),
      options: storeOptions,
    }),
    // TCR-2629 — miền giao hàng: đi kèm chi nhánh vì cả hai đều là lát cắt theo địa
    // bàn (chi nhánh bán ở đâu, đơn giao tới miền nào).
    createSelectFilter({
      key: 'delivery_region',
      label: 'Miền giao hàng',
      placeholder: 'Tất cả miền',
      width: '100%',
      value: filters.delivery_region,
      onChange: (v) => onApplyFilter({ delivery_region: v }),
      options: [...DELIVERY_REGION_OPTIONS],
    }),
    // TCR-2130 — lọc đơn kênh GT (khách trả trước) ở mọi kênh bán, phục vụ danh sách chờ duyệt.
    createSelectFilter({
      key: 'b2b_channel',
      label: 'Kênh khách B2B',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.b2b_channel,
      onChange: (v) => onApplyFilter({ b2b_channel: v }),
      options: [...ORDER_B2B_CHANNEL_FILTER_OPTIONS],
    }),
    // TCR-2129 — kế toán lọc đơn có CK khác chính sách khi duyệt.
    createSelectFilter({
      key: 'has_discount_deviation',
      label: 'Chiết khấu',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.has_discount_deviation,
      onChange: (v) => onApplyFilter({ has_discount_deviation: v }),
      options: [{ value: 'true', label: 'Đơn có chiết khấu khác chính sách' }],
    }),
    // KHÔNG có filter "Trạng thái đơn hàng" ở đây: hàng Tabs phía trên (`status_group`) đã
    // là chỗ chọn trạng thái, để thêm select `status` trong drawer gây hai nguồn trạng thái
    // chồng nhau. Key `status` vẫn giữ trong ORDER_FILTER_KEYS/ActiveFilterTags để URL cũ
    // (và nút xoá tag) còn xử lý được.
    createSelectFilter({
      key: 'created_by',
      label: 'Người tạo đơn',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.created_by,
      onChange: (v) => onApplyFilter({ created_by: v }),
      options: staffOptions,
    }),
    createSelectFilter({
      key: 'payment_state',
      label: 'Trạng thái thanh toán',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.payment_state,
      onChange: (v) => onApplyFilter({ payment_state: v }),
      options: [...ORDER_PAYMENT_STATE_FILTER_OPTIONS],
    }),
    // TCR-1967 — đặt ngay sau "Trạng thái thanh toán" theo yêu cầu ticket.
    createSelectFilter({
      key: 'invoice_state',
      label: 'Trạng thái hoá đơn',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.invoice_state,
      onChange: (v) => onApplyFilter({ invoice_state: v }),
      options: [...ORDER_INVOICE_STATE_FILTER_OPTIONS],
    }),
    createSelectFilter({
      key: 'fee_payer',
      label: 'Người trả phí vận chuyển',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.fee_payer,
      onChange: (v) => onApplyFilter({ fee_payer: v }),
      options: [
        { label: 'Người bán', value: 'seller' },
        { label: 'Người mua', value: 'buyer' },
      ],
    }),
    createSelectFilter({
      key: 'has_customer_note',
      label: 'Ghi chú khách hàng',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.has_customer_note,
      onChange: (v) => onApplyFilter({ has_customer_note: v }),
      options: [
        { label: 'Có ghi chú', value: 'true' },
        { label: 'Không ghi chú', value: 'false' },
      ],
    }),
    createSelectFilter({
      key: 'prepaid',
      label: 'Tạm ứng',
      placeholder: 'Tất cả',
      width: '100%',
      value: filters.prepaid,
      onChange: (v) => onApplyFilter({ prepaid: v }),
      options: [
        { label: 'Đã trả trước', value: 'true' },
        { label: 'Chưa trả trước', value: 'false' },
      ],
    }),
  ];
}
